import { useState } from 'react';
import { Link } from 'react-router-dom';
import { geometryTrigQuestions } from './data/geometryTrigQuestions';
import SATQuestionCard from '../components/SATQuestionCard';
import ProgressBar from '../algebra-practice/components/ProgressBar';
import { useScoreHistory } from '../../../hooks/useScoreHistory';

export default function GeometryTrigPracticePage() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<(string | null)[]>(
    new Array(geometryTrigQuestions.length).fill(null)
  );
  const [showResults, setShowResults] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const { saveScore } = useScoreHistory();

  const current = geometryTrigQuestions[currentIndex];
  const total = geometryTrigQuestions.length;
  const answeredCount = userAnswers.filter((a) => a !== null).length;
  const isLast = currentIndex === total - 1;

  const handleAnswerSelect = (answer: string) => {
    const updated = [...userAnswers];
    updated[currentIndex] = answer;
    setUserAnswers(updated);
  };

  const handleNext = () => {
    if (isLast) {
      // Calculate and save score
      const correctCount = userAnswers.filter(
        (ans, i) => ans === geometryTrigQuestions[i].correctAnswer
      ).length;
      const percentage = Math.round((correctCount / total) * 100);
      
      saveScore({
        examName: 'SAT Math: Geometry & Trigonometry',
        domain: 'Geometry & Trigonometry',
        examType: 'math',
        score: correctCount,
        total,
        percentage,
      });
      
      setShowResults(true);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleRetake = () => {
    setUserAnswers(new Array(total).fill(null));
    setCurrentIndex(0);
    setShowResults(false);
    setReviewMode(false);
  };

  const handleReview = () => {
    setReviewMode(true);
    setShowResults(false);
    setCurrentIndex(0);
  };

  const score = userAnswers.filter(
    (ans, i) => ans === geometryTrigQuestions[i].correctAnswer
  ).length;
  const percentage = Math.round((score / total) * 100);

  const subtopicBreakdown = (() => {
    const map: Record<string, { correct: number; total: number }> = {};
    geometryTrigQuestions.forEach((q, i) => {
      if (!map[q.subtopic]) map[q.subtopic] = { correct: 0, total: 0 };
      map[q.subtopic].total++;
      if (userAnswers[i] === q.correctAnswer) map[q.subtopic].correct++;
    });
    return Object.entries(map).map(([subtopic, stats]) => ({ subtopic, ...stats }));
  })();

  const getPerformance = () => {
    if (percentage >= 90) return { text: 'Outstanding!', color: 'text-emerald-600', emoji: '🎉' };
    if (percentage >= 80) return { text: 'Excellent Work!', color: 'text-emerald-600', emoji: '🌟' };
    if (percentage >= 70) return { text: 'Good Job!', color: 'text-teal-600', emoji: '👍' };
    if (percentage >= 60) return { text: 'Keep Practicing!', color: 'text-yellow-600', emoji: '💪' };
    return { text: 'Review & Try Again', color: 'text-orange-600', emoji: '📚' };
  };

  const perf = getPerformance();

  if (showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-12 px-4">
        <div className="max-w-4xl mx-auto mb-6">
          <Link
            to="/sat-prep"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to SAT Prep
          </Link>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">{perf.emoji}</div>
            <h2 className={`text-4xl font-bold mb-2 ${perf.color}`}>{perf.text}</h2>
            <p className="text-gray-600 text-lg">Geometry &amp; Trigonometry complete!</p>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-8 text-white mb-6 shadow-lg">
            <div className="text-center mb-6">
              <div className="text-7xl font-bold mb-2">{percentage}%</div>
              <div className="text-xl opacity-90">Your Score</div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/20 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold">{score}</div>
                <div className="text-sm opacity-90">Correct</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold">{total - score}</div>
                <div className="text-sm opacity-90">Incorrect</div>
              </div>
              <div className="bg-white/20 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold">{total}</div>
                <div className="text-sm opacity-90">Total</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-bar-chart-line text-amber-600"></i>
              Performance by Subtopic
            </h3>
            <div className="space-y-4">
              {subtopicBreakdown.map((item, idx) => {
                const pct = Math.round((item.correct / item.total) * 100);
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-700 text-sm">{item.subtopic}</span>
                      <span className="text-sm font-semibold text-gray-600">
                        {item.correct}/{item.total} ({pct}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${pct}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleReview}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-list-3-line text-xl"></i>
              Review All Answers
            </button>
            <button
              onClick={handleRetake}
              className="flex-1 bg-white hover:bg-gray-50 text-gray-800 font-semibold py-4 rounded-lg border-2 border-gray-300 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line text-xl"></i>
              Retake Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-12 px-4">
      <div className="max-w-4xl mx-auto mb-8">
        <Link
          to="/sat-prep"
          className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold mb-6 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          Back to SAT Prep
        </Link>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                SAT Math: Geometry &amp; Trigonometry
              </h1>
              <p className="text-gray-600">
                20 questions covering area, volume, coordinate geometry, triangles, and trig
              </p>
            </div>
            {reviewMode && (
              <div className="flex items-center gap-3 ml-4">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                  Review Mode
                </span>
                <button
                  onClick={() => setShowResults(true)}
                  className="text-sm text-gray-600 hover:text-gray-800 underline whitespace-nowrap cursor-pointer"
                >
                  Exit Review
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!reviewMode && (
        <ProgressBar current={currentIndex + 1} total={total} answered={answeredCount} />
      )}

      <SATQuestionCard
        question={current}
        questionNumber={currentIndex + 1}
        totalQuestions={total}
        selectedAnswer={userAnswers[currentIndex]}
        onAnswerSelect={handleAnswerSelect}
        showSolution={reviewMode || userAnswers[currentIndex] !== null}
      />

      <div className="max-w-4xl mx-auto mt-6 flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Previous
        </button>

        {reviewMode ? (
          <div className="flex gap-3">
            {currentIndex < total - 1 && (
              <button
                onClick={() => setCurrentIndex((i) => i + 1)}
                className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Next Question
                <i className="ri-arrow-right-line"></i>
              </button>
            )}
            {currentIndex === total - 1 && (
              <button
                onClick={() => setShowResults(true)}
                className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Back to Results
              </button>
            )}
          </div>
        ) : (
          <button
            onClick={handleNext}
            disabled={userAnswers[currentIndex] === null}
            className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            {isLast ? 'Submit Test' : 'Next'}
            <i className={isLast ? 'ri-check-line' : 'ri-arrow-right-line'}></i>
          </button>
        )}
      </div>

      {reviewMode && (
        <div className="max-w-4xl mx-auto mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Jump to Question:</h3>
          <div className="grid grid-cols-10 gap-2">
            {geometryTrigQuestions.map((q, idx) => {
              const isCorrect = userAnswers[idx] === q.correctAnswer;
              return (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-10 h-10 rounded-lg font-semibold text-sm transition-all cursor-pointer ${
                    currentIndex === idx
                      ? 'bg-amber-600 text-white ring-2 ring-amber-300'
                      : isCorrect
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  }`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
